/**
 * Перенос состояния сцены в DOM.
 *
 * Отдельный модуль, потому что это единственное место, где значение
 * из сети встречается со свойством style. Такое место должно быть
 * ровно одно и должно быть проверяемо.
 *
 * МАСШТАБИРОВАНИЕ. Сцена живёт в собственных единицах (обычно
 * 1920×1080), а Browser Source бывает какого угодно размера: 2560
 * на большом мониторе, 1280 у того, кто экономит ресурсы, и совсем
 * мелкий в предпросмотре панели. Вместо пересчёта каждого слоя
 * растягивается ОДИН корневой узел: `transform: scale()`. Отсюда
 * два следствия — координаты в протоколе не зависят от размера
 * источника, и пересчитывать при изменении размера нечего, кроме
 * одного числа (docs/01).
 */

import type { Layer as SceneLayer } from '@owm/protocol';
import {
  clock,
  EMBED_STATE_EVENT,
  Host,
  primitives,
  sketchLayer,
  CameraWall,
  type EmbedStateDetail,
  type Layer as HostLayer,
  type CameraStreamFrame,
  type CameraStreamSource,
  type LayerCommand,
  type ShapeKind,
} from '@owm/widgets';

import {
  NO_EMBEDS,
  playbackAt,
  RUNNING,
  typeName,
  withEmbedURL,
  type EmbedSources,
  type LiveTransform,
  type Playback,
  type SceneState,
  type SketchUpdate,
  type StopState,
} from '@owm/scene';

/**
 * Глубина рисунка в работе.
 *
 * Заведомо выше любого слоя сцены: рисуют поверх кадра, глядя
 * на то, что уже в нём. Спрятать штрих под картинку значило бы
 * вести руку вслепую.
 *
 * ЧИСЛО ОБЫЧНОЕ, А НЕ MAX_SAFE_INTEGER, хотя соблазн был. z-index
 * в CSS — это ЦЕЛОЕ СО ЗНАКОМ В 32 БИТА, и объявление с числом
 * за его пределом браузер вправе отбросить целиком: глубина стала
 * бы `auto`, то есть никакой. Держалось бы это на одном лишь
 * порядке узлов в DOM — до первого слоя, который окажется после.
 */
const SKETCH_Z = 1_000_000;

/** Что нужно знать об источнике, чтобы вписать в него сцену. */
export interface Viewport {
  readonly width: number;
  readonly height: number;
}

/**
 * Renderer держит DOM оверлея в соответствии со сценой.
 *
 * Состояние (какие слои созданы) живёт в Host из @owm/widgets —
 * общем с панелью. Предпросмотр обязан выглядеть так же, как эфир,
 * а два независимых рендерера разошлись бы на первой же правке.
 */
export class Renderer {
  private readonly canvas: HTMLElement;
  private readonly host: Host;
  private viewport: Viewport = { width: 0, height: 0 };
  private canvasSize = { w: 0, h: 0 };
  /**
   * Источники эмбедов: в сцене их адресов нет вовсе (docs/05),
   * и слой `embed` знает только идентификатор источника.
   */
  private embeds: EmbedSources = NO_EMBEDS;
  /** Что сейчас перекрыто владельцем. */
  private stop: StopState = RUNNING;
  /*
     ТРАНЗИЕНТЫ ЖИВУТ ОТДЕЛЬНО ОТ СЦЕНЫ, И ЭТО ГЛАВНОЕ ЗДЕСЬ.

     Пока модератор держит мышь, положение слоя приходит до шестидесяти
     раз в секунду и версии не меняет. Положи мы это в состояние —
     оверлей считал бы себя ушедшим вперёд, а следующая настоящая
     дельта пришла бы с «неожиданной» версией и вызвала бы resync
     на каждое движение мыши.

     Поэтому здесь отдельная карта: слой рисуется по ней, если она
     про него что-то знает, и по сцене во всех остальных случаях.
  */
  private live = new Map<string, LiveTransform>();

  /*
    Рисунки в работе — по одному на рисующего.
    Ключ не слой, а тот, кто ведёт руку: слоя ещё нет, а рисующих
    может быть несколько сразу.
  */
  private sketches = new Map<string, HostLayer>();

  /*
    Камеры модераторов: декодеры и отрисовка в холсты слоёв.

    ЖИВЁТ У РЕНДЕРЕРА, потому что рисует в узлы слоёв, а держит
    их хост виджетов. Отдельно от сцены: слой камеры лежит в сцене,
    а картинка — транзиент, которого в сцене нет вовсе.
  */
  private readonly cameras: CameraWall;
  /*
     ВЗЯТЫЕ РУКОЙ СЛОИ ДЕРЖАТСЯ СМОНТИРОВАННЫМИ, даже стоя за кадром.

     Обычное правило — создавать по пересечению с кадром — означает,
     что слой, внесённый рывком из-за края, рождается ровно в миг
     пересечения: узел создаётся и картинка декодируется тогда же,
     и зритель видит, как она догоняет курсор.

     Взятие руки известно ЗАРАНЕЕ: модератор уже держит слой, и то,
     что тот вот-вот окажется в кадре, — почти решённое дело.
  */
  private held = new Set<string>();

  /*
    Состояние, ждущее ближайшего кадра, и признак того, что кадр уже
    заказан (см. renderLater).

    Хранится ПОСЛЕДНЕЕ, а не очередь: промежуточные положения одного
    движения руки экран всё равно не покажет, и рисовать их значит
    делать работу, результат которой затрут через долю миллисекунды.
  */
  private pending: SceneState | null = null;
  private scheduled = false;

  constructor(
    private readonly root: HTMLElement,
    onError?: (layerID: string, error: unknown) => void,
    /**
     * Куда сообщать о состоянии эмбеда.
     *
     * Отвалившийся эмбед оставляет в эфире пустое место — и это
     * правильно: сообщение об ошибке поверх кадра увидели бы
     * зрители. Но тогда стример не узнает о поломке вовсе, если
     * не смотрит в эфир. Отсюда рассказ панели.
     */
    onEmbedState?: (layerID: string, state: string) => void,
    /**
     * Чем планировать отложенную перерисовку (см. renderLater).
     *
     * Подменяется в тестах: ждать настоящего кадра значит завязать
     * проверку на планировщик браузера и на то, идёт ли вкладка
     * вообще — в фоне requestAnimationFrame не срабатывает.
     */
    private readonly schedule: (fn: () => void) => void = (fn) => {
      requestAnimationFrame(fn);
    },
  ) {
    // Отдельный узел под сцену: корень занимает всё окно, а канва
    // масштабируется. Смешать их значило бы масштабировать и то,
    // что к сцене не относится, — например, сообщение о поломке
    this.canvas = document.createElement('div');
    this.canvas.dataset.role = 'canvas';
    this.canvas.style.position = 'absolute';
    this.canvas.style.left = '0';
    this.canvas.style.top = '0';
    // Точка отсчёта в левом верхнем углу: иначе масштабирование
    // раздвигало бы сцену от центра, и слой с координатой (0,0)
    // уезжал бы за край
    this.canvas.style.transformOrigin = '0 0';

    // Событие всплывает от слоя: слушателя хватает одного на всю
    // сцену, а слои приходят и уходят. Вешать его на каждый узел
    // значило бы снимать при каждом уничтожении и однажды забыть
    if (onEmbedState) {
      this.canvas.addEventListener(EMBED_STATE_EVENT, (event) => {
        const layerID = (event.target as HTMLElement | null)?.dataset.layerId;
        if (layerID === undefined) return;

        onEmbedState(layerID, (event as CustomEvent<EmbedStateDetail>).detail.state);
      });
    }

    /*
      СТЕНА КАМЕР ЖИВЁТ НАД КАНВОЙ СЦЕНЫ, а не над корнем: холсты
      камер — это узлы слоёв, и искать их надо там же, где они
      рисуются.

      Ошибка декодера уходит туда же, куда ошибка слоя: молчащая
      камера не должна останавливать остальную сцену, но и молчать
      о себе не должна.
    */
    this.cameras = new CameraWall(this.canvas, (source, error) => {
      onError?.(`камера ${source}`, error);
    });

    this.root.append(this.canvas);
    this.host = new Host({
      root: this.canvas,
      widgets: primitives,
      ...(onError ? { onError } : {}),
    });
  }

  /**
   * Принимает список источников эмбедов.
   *
   * Отдельно от render, потому что приходят они отдельным
   * сообщением и живут дольше сцены: снапшот присылают заново
   * при каждой пересинхронизации, а источники — один раз
   * при подключении.
   */
  setEmbeds(sources: EmbedSources): void {
    this.embeds = sources;
  }

  /**
   * Принимает состояние стоп-крана.
   *
   * Отдельно от сцены и от источников: это не то, что канал
   * настроил, а то, что владелец перекрыл прямо сейчас. Живёт оно
   * дольше снапшота и приходит до первого кадра — оверлей
   * переподключается посреди аварии.
   */
  setStop(state: StopState): void {
    this.stop = state;
  }

  /**
   * Отбирает слои, которые вообще попадут в DOM.
   *
   * ЭМБЕДЫ ПРИ «ЗАГЛУШИТЬ ВСЁ» ВЫБРАСЫВАЮТСЯ ИЗ СПИСКА, а не
   * прячутся. Разница здесь не косметическая, и docs/05 говорит
   * о ней прямо: `display: none` не гарантирует остановку
   * воспроизведения. Звук чужой страницы идёт мимо нашего гейна —
   * она играет под своим origin, и дотянуться до неё из JS нельзя
   * вовсе. Единственный способ заставить её замолчать — выгрузить.
   *
   * Слой, пропавший из списка, host уничтожает: узел iframe уходит
   * из DOM вместе с ним. Побочный эффект — чат потеряет накопленную
   * историю, а алерты переподключатся; для аварийной кнопки это
   * приемлемо и написано на самой кнопке.
   */
  private visible(layers: readonly SceneLayer[]): readonly SceneLayer[] {
    if (!this.stop.silence) return layers;

    return layers.filter((layer) => typeName(layer.type) !== 'embed');
  }

  /** Пересчитывает вписывание под новый размер источника. */
  resize(viewport: Viewport): void {
    this.viewport = viewport;
    this.applyScale();
  }

  /** Переносит состояние сцены в DOM. */
  /**
   * Перерисовка не чаще кадра экрана.
   *
   * ЗАЧЕМ. Транзиентное движение приходит до шестидесяти раз
   * в секунду НА КАЖДЫЙ удерживаемый слой, и каждое сообщение
   * вызывало полную пересборку сцены: разбор свойств и семь записей
   * в style на каждый видимый слой. Тридцать слоёв в кадре и тройка
   * под мышью — это тысячи разборов и десятки тысяч записей в стиль
   * в секунду ради того, что сдвинулся один прямоугольник.
   *
   * Кадры одного движения руки приходят подряд, за доли
   * миллисекунды, и рисовать промежуточные незачем: экран всё равно
   * покажет последний.
   *
   * НЕ ЗАМЕНЯЕТ render. Стоп-кран и приезд источников эмбедов рисуют
   * сразу, и это не придирчивость: «заглушить всё» выгружает чужие
   * страницы из DOM, и отложить это значило бы оставить их звучать
   * до следующего кадра.
   */
  renderLater(state: SceneState): void {
    this.pending = state;
    if (this.scheduled) return;

    this.scheduled = true;
    this.schedule(() => {
      this.scheduled = false;

      const next = this.pending;
      this.pending = null;
      if (next !== null) this.render(next);
    });
  }

  render(state: SceneState): void {
    // Отложенной работы больше нет: то, что она собиралась нарисовать,
    // рисуется прямо сейчас, и повторять это на ближайшем кадре
    // значило бы делать её дважды
    this.pending = null;

    if (state.scene === null) {
      this.host.clear();
      return;
    }

    const { canvasW, canvasH } = state.scene;
    if (canvasW !== this.canvasSize.w || canvasH !== this.canvasSize.h) {
      this.canvasSize = { w: canvasW, h: canvasH };
      this.applyScale();
    }

    this.host.sync(
      [
        ...this.visible(state.scene.layers).map((layer) =>
          toHostLayer(
            layer,
            this.embeds,
            this.stop,
            this.live.get(layer.id),
            this.held.has(layer.id),
          ),
        ),
        // Рисунки в работе идут ПОСЛЕДНИМИ и поверх всего: их ведут
        // прямо сейчас, и спрятать то, на что смотрит рука, значило бы
        // рисовать вслепую
        ...this.sketches.values(),
      ],
      { w: canvasW, h: canvasH },
    );
  }

  /**
   * Слой взяли рукой или отпустили.
   *
   * Отпущенный возвращается к общему правилу: уехал за кадр —
   * уничтожается. Держать его вечно значило бы копить узлы
   * припаркованного, которого в эфире никто не увидит.
   */
  setHeld(layerID: string, held: boolean): void {
    if (held) this.held.add(layerID);
    else this.held.delete(layerID);
  }

  /** Слой поехал: показываем его там, где он сейчас под мышью. */
  setLive(layerID: string, transform: LiveTransform): void {
    this.live.set(layerID, transform);
  }

  /**
   * Сосед ведёт рисунок: показываем его по ходу.
   *
   * СТРОИТСЯ ОБЩЕЙ АРИФМЕТИКОЙ (@owm/widgets, sketchLayer) — той же,
   * которой панель соберёт настоящий слой на отпускании. Своя вторая
   * реализация разошлась бы молча, и разницу увидел бы зритель:
   * рисунок в работе стоял бы не там, где встанет готовый.
   *
   * Негодное сюда не доходит: разбор отсеял его в @owm/scene.
   */
  setSketch(sketch: SketchUpdate): void {
    const points: { x: number; y: number }[] = [];
    for (let i = 0; i + 1 < sketch.points.length; i += 2) {
      points.push({ x: sketch.points[i]!, y: sketch.points[i + 1]! });
    }

    const shape = sketchLayer(sketch.kind as ShapeKind, points, sketch.color, sketch.width);
    if (shape === null) return;

    this.sketches.set(sketch.clientId, {
      id: `sketch:${sketch.clientId}`,
      type: 'shape',
      rect: shape.rect,
      hidden: this.stop.hide,
      z: SKETCH_Z,
      props: { ...shape.props, points: [...shape.props.points] },
    });
  }

  /**
   * Кто сейчас вещает камерой.
   *
   * Списком целиком, а не «этот включился»: потерявший одно
   * сообщение иначе остался бы с неверным набором навсегда,
   * а список чинит себя сам на следующем изменении.
   */
  setCameras(sources: readonly CameraStreamSource[]): void {
    this.cameras.setSources(sources);
  }

  /** Кадр камеры: декодируем и рисуем в холсты её слоёв. */
  cameraFrame(frame: CameraStreamFrame): void {
    this.cameras.frame(frame);
  }

  /**
   * Забывает рисунки в работе.
   *
   * Зовётся по сроку, а не по сообщению «я закончил»: такое
   * сообщение пришло бы РАНЬШЕ настоящей операции — транзиент летит
   * напрямую, а операция идёт через запись в базу, — и рисунок
   * мигнул бы: пропал и появился.
   */
  clearSketches(): void {
    this.sketches.clear();
  }

  /**
   * Забывает транзиенты.
   *
   * Зовётся, когда пришло НАСТОЯЩЕЕ положение — снапшот или дельта.
   * Оставить транзиент поверх него значило бы показывать зрителю
   * то место, где слой побывал по дороге, вместо того, где он есть.
   *
   * Чистится всё сразу, а не только пришедшие слои: сосед, который
   * тянет свой слой прямо сейчас, пришлёт следующее положение через
   * шестнадцать миллисекунд, и его движение не прервётся заметно.
   * Разбирать, каких слоёв касалась дельта, ради этого не стоит.
   */
  clearLive(): void {
    this.live.clear();
  }

  /** Передаёт слою команду воспроизведения. */
  command(layerID: string, name: LayerCommand, positionMs?: number): boolean {
    return this.host.command(layerID, name, positionMs);
  }

  /**
   * Приводит ролик к общему состоянию.
   *
   * Позиция считается ЗДЕСЬ, по серверным часам: виджет не знает
   * ни про сеть, ни про поправку и знать не должен. Петля учитывается
   * длиной, которую знает только проигрыватель, — поэтому длина
   * спрашивается у него.
   */
  playback(state: Playback): boolean {
    const media = this.mediaOf(state.layerId);

    return this.host.playback(state.layerId, {
      playing: state.playing,
      positionMs: playbackAt(state, clock().now(), {
        durationMs: media !== null && Number.isFinite(media.duration) ? media.duration * 1000 : 0,
        loop: media?.loop ?? false,
      }),
    });
  }

  /** Проигрыватель слоя, если он создан. */
  private mediaOf(layerID: string): HTMLMediaElement | null {
    const node = this.root.querySelector(`[data-layer-id="${layerID}"] video`);

    return node instanceof HTMLMediaElement ? node : null;
  }

  /**
   * Пересоздаёт узел одного слоя по просьбе панели.
   *
   * Нужно эмбедам: чужая страница умеет зависать, а починить её
   * изнутри мы не можем — фрейм чужой. Остальные слои при этом
   * не трогаются: пересоздать всю сцену было бы проще, но одна
   * залипшая рамка стоила бы перезапуска соседнего видео.
   */
  reload(layerID: string): boolean {
    return this.host.reload(layerID);
  }

  /** Сколько слоёв сейчас в DOM. Для тестов и отладки. */
  get size(): number {
    return this.host.size;
  }

  /**
   * Вписывает канву в источник.
   *
   * Множитель ОДИН на обе оси — берётся меньший. Разные множители
   * растянули бы картинку: круг стал бы овалом, а текст — сплющенным.
   * Пустое место по краям честнее искажения: по нему сразу видно,
   * что размер источника не совпадает с кадром, и это можно
   * исправить в OBS.
   */
  private applyScale(): void {
    const { w, h } = this.canvasSize;
    if (w <= 0 || h <= 0 || this.viewport.width <= 0 || this.viewport.height <= 0) {
      return;
    }

    const scale = Math.min(this.viewport.width / w, this.viewport.height / h);

    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;
    // scale, а не изменение width/height каждого слоя: композитор
    // применяет трансформацию на видеокарте, а пересчёт размеров
    // заставил бы браузер заново раскладывать всю страницу
    this.canvas.style.transform = `scale(${scale})`;
  }
}

/** Переводит слой протокола в то, что понимает Host. */
/*
ЧТО ДЕШЁВО ДЕРЖАТЬ СМОНТИРОВАННЫМ ЗА КАДРОМ.

Всё, кроме того, что играет или грузит чужое. Припаркованное видео
продолжало бы декодироваться, звук — играть, а рамка эмбеда —
держать открытой чужую страницу, съедая процессор стримера в эфире
(docs/10, 2.11). Картинка, текст и фигура за кадром не стоят ничего.
*/
const COSTLY_OFFSCREEN = new Set(['video', 'embed']);

function toHostLayer(
  layer: SceneLayer,
  embeds: EmbedSources,
  stop: StopState,
  live?: LiveTransform,
  held = false,
): HostLayer {
  // Транзиент важнее сцены: он и есть то, где слой прямо сейчас.
  // Сцена в этот момент отстаёт на один жест
  const t = live ?? layer.transform ?? { x: 0, y: 0, w: 0, h: 0, rotation: 0 };

  return {
    id: layer.id,
    type: typeName(layer.type),
    rect: { x: t.x, y: t.y, w: t.w, h: t.h },
    rotation: t.rotation,
    // Зеркало берётся у СЦЕНЫ, а не у транзиента: жест его не задаёт
    // и в сообщении live его нет вовсе — возьми мы его отсюда,
    // отражённый слой разворачивался бы обратно на время чужого
    // перетаскивания
    flipH: layer.transform?.flipH ?? false,
    flipV: layer.transform?.flipV ?? false,
    // «Скрыть всё» прячет слой, не трогая сцену: в состоянии он
    // по-прежнему видим, и снять кнопку можно одним нажатием.
    // Проставить hidden каждому слою операциями значило бы забить
    // журнал и стек отмены — и не суметь вернуть как было: часть
    // слоёв была спрятана и до аварии
    hidden: layer.hidden || stop.hide,
    // Держим заранее только взятое рукой и только то, что держать
    // дёшево: см. COSTLY_OFFSCREEN
    keepMounted: held && !COSTLY_OFFSCREEN.has(typeName(layer.type)),
    z: layer.z,
    // Адрес эмбеда подставляется ЗДЕСЬ, на последнем шаге перед
    // отрисовкой. Слоям других типов withEmbedURL ничего не делает:
    // у них нет sourceId
    props: withEmbedURL(layer.props ?? {}, embeds),
  };
}
